import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import Modal from 'LuxKit/Modal/Modal'
import { copyValueToClipboard, copyAvailable } from 'lib/web/clipboardUtils'
import { offerShareButtonClick, offerShareComplete } from 'actions/OfferActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { trackClientEvent } from 'analytics/analytics'
import ShareByEmailForm from './ShareByEmailForm'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Group from 'components/utils/Group'
import ShareThis, { ShareOption } from 'components/Vendor/ShareThis'
import BodyText from 'LuxKit/Typography/BodyText'
import LineEnvelopeIcon from 'LuxKit/Icons/line/LineEnvelopeIcon'
import TextButton from 'LuxKit/Button/TextButton'
import LineLinkIcon from 'LuxKit/Icons/line/LineLinkIcon'
import styled from 'styled-components'
import { rem } from 'polished'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import { postMessageToWhiteLabelApp } from 'lib/whitelabels/whitelabels'
import useShareVariant from 'hooks/useShareVariant'
import type { ShareVariant } from 'hooks/useShareVariant'
import useHashSignal from 'hooks/useHashSignal'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import { SHARE_SOCIETE_POINTS_BY_VARIANT } from 'constants/experimentConfig'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import StackedLoyaltyTierIcon from 'LuxKit/Icons/loyalty/StackedLoyaltyTierIcon'
export const EMAIL_MODE = 'email'

const ShareLinkTextContainer = styled.div`
  cursor: pointer;
  background: ${props => props.theme.palette.neutral.default.six};
  overflow: hidden;
  padding: ${rem(12)};
`

const CopyLinkOuterContainer = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  overflow: hidden;
`

export interface ShareConfig {
  url?: string
  customerPortalURL?: string
  memberId?: string
  brand?: string
  holidaySynonym?: string
  image?: App.Image
  offerId?: string
  offerLocation?: string
  pathName?: string
  region?: string
  type?: string
  utmCampaign?: string
  shareTitle?: string
  mailToSubject?: string
  pageId?: string
  pageType?: string
  onSend?: (state: { friend_email: string, message?: string }) => Promise<any>
  shareMessage?: string
  mailToObject?: string
  shareVariant?: ShareVariant
}

interface Props {
  loggedIn?: boolean
  shareConfig: ShareConfig
  children?: React.ReactElement
  offerShareButtonClick: (...params: Parameters<typeof offerShareButtonClick>) => void
  offerShareComplete: (...params: Parameters<typeof offerShareComplete>) => void
  onShare?: (network: string) => void
  mode?: typeof EMAIL_MODE
  /** When provided, the modal will open automatically when this hash is present in the URL */
  openOnHash?: `#${string}`
  /** When true, hides the email share option from the modal */
  hideEmail?: boolean
}

function ShareModal(props: Props) {
  const {
    shareConfig,
    children,
    loggedIn,
    offerShareButtonClick,
    offerShareComplete,
    onShare: onShareProp,
    mode: initialMode,
    openOnHash,
    hideEmail,
  } = props
  const intl = useIntl()
  const { variation } = useExperiment(Experiments.croShareSocietePoints)
  const sharePoints = variation ? SHARE_SOCIETE_POINTS_BY_VARIANT[variation] : undefined

  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<typeof EMAIL_MODE | undefined>(initialMode)
  const whiteLabelAppConfig = useWhiteLabelAppConfig()

  // Open modal when hash is detected
  const openModalFromHash = useCallback(() => {
    setIsOpen(true)
  }, [])

  const { hashSignalActive, clearHash } = useHashSignal({
    hash: openOnHash ?? '#share',
    enabled: !!openOnHash,
    onActivate: openModalFromHash,
  })

  const closeShareModal = useCallback(() => {
    setIsOpen(false)
    setMode(undefined)
    // Clear hash from URL when modal is closed
    if (hashSignalActive) {
      clearHash()
    }
  }, [hashSignalActive, clearHash])

  const computedShareVariant = useShareVariant()
  const shareVariant: ShareVariant = shareConfig.shareVariant ?? computedShareVariant

  const shareUrl = useMemo(() => {
    if (!shareConfig.url) return undefined
    if (shareVariant !== 'nativeShare') return shareConfig.url
    try {
      const updatedUrl = new URL(shareConfig.url)
      updatedUrl.searchParams.set('share_variant', shareVariant)
      return updatedUrl.toString()
    } catch {
      return shareConfig.url
    }
  }, [shareConfig.url, shareVariant])

  const resolvedShareConfig = useMemo(() => {
    if (shareVariant === 'nativeShare') {
      if (shareUrl && (shareConfig.url !== shareUrl || shareConfig.shareVariant !== 'nativeShare')) {
        return { ...shareConfig, url: shareUrl, shareVariant: 'nativeShare' as const }
      }
    } else {
      if (shareConfig.shareVariant !== 'modalShare') {
        return { ...shareConfig, shareVariant: 'modalShare' as const }
      }
    }
    if (shareConfig.shareVariant === undefined && shareVariant === 'modalShare') {
      return { ...shareConfig, shareVariant }
    }
    return shareConfig
  }, [shareConfig, shareUrl, shareVariant])

  const nativeShareSupported = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  const shouldUseNativeShare = shareVariant === 'nativeShare' && nativeShareSupported

  const trackShareButtonClick = useCallback(() => {
    if (resolvedShareConfig.pageId && resolvedShareConfig.pageType) {
      offerShareButtonClick(
        resolvedShareConfig.pageId,
        resolvedShareConfig.pageType,
      )
    }
  }, [offerShareButtonClick, resolvedShareConfig.pageId, resolvedShareConfig.pageType])

  const openShareModal = useCallback(() => {
    setIsOpen(true)
  }, [])

  const onShare = useCallback((shareMethod: string, shareMethodKey?: string, closeModal: boolean = true) => {
    if (resolvedShareConfig.pageId && resolvedShareConfig.pageType) {
      offerShareComplete(
        resolvedShareConfig.pageId,
        resolvedShareConfig.pageType,
        shareMethod,
      )
    }
    trackClientEvent({
      subject: 'share',
      action: shareMethodKey ?? shareMethod,
      category: 'offer_page',
      type: 'interaction',
    })
    onShareProp?.(shareMethod)
    if (closeModal) closeShareModal()
  }, [offerShareComplete, resolvedShareConfig.pageId, resolvedShareConfig.pageType, onShareProp, closeShareModal])

  const mailTo = useMemo(() => {
    if (loggedIn) return
    const urlForEmail = shareUrl ?? resolvedShareConfig.url
    if (!urlForEmail) return
    const body = encodeURIComponent(urlForEmail)
    const subject = resolvedShareConfig.mailToSubject
    return `mailto:?subject=${subject}&body=${body}`
  }, [loggedIn, resolvedShareConfig.mailToSubject, resolvedShareConfig.url, shareUrl])

  const copyToClipboard = useCallback(() => {
    const urlToCopy = shareUrl ?? resolvedShareConfig.url
    if (urlToCopy) {
      copyValueToClipboard(urlToCopy)
      showSnackbar(intl.formatMessage({
        id: 'uyrj5lrm7aTCsQMuTN72',
        defaultMessage: 'Link copied to clipboard',
        description: 'Success message shown after copying share link to clipboard',
      }), 'success')
    }
    onShare('clipboard', undefined, false)
  }, [onShare, resolvedShareConfig.url, shareUrl, intl])

  const handleNativeShare = useCallback(async(): Promise<boolean> => {
    if (!shouldUseNativeShare) return false
    const urlToShare = shareUrl ?? resolvedShareConfig.url
    if (!urlToShare) return false

    try {
      await navigator.share({
        title: resolvedShareConfig.shareTitle,
        text: resolvedShareConfig.shareMessage,
        url: urlToShare,
      })
      onShare('native_share', undefined, false)
      return true
    } catch(error) {
      if ((error as DOMException)?.name === 'AbortError') {
        return true
      }
      return false
    }
  }, [shouldUseNativeShare, resolvedShareConfig.shareTitle, resolvedShareConfig.shareMessage, resolvedShareConfig.url, shareUrl, onShare])

  const buttonChild = React.Children.only(children) as React.ReactElement<{ onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }>
  const ShareButton = buttonChild ? React.cloneElement(buttonChild, {
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      const originalOnClick = buttonChild.props.onClick
      if (originalOnClick) {
        originalOnClick(e)
      }

      if (whiteLabelAppConfig.isApp) {
        postMessageToWhiteLabelApp({ shareOffer: true })
        return
      }

      trackShareButtonClick()

      if (shouldUseNativeShare) {
        handleNativeShare().then((didShare) => {
          if (!didShare) {
            openShareModal()
          }
        })
        return
      }

      openShareModal()
    },
  }) : null

  const modalTitle = useMemo(() => {
    if (!resolvedShareConfig.pageType) {
      return intl.formatMessage({
        id: 'sg1Os4nm-7MT_TptPH-k',
        defaultMessage: 'Share',
        description: 'Generic share modal title when no page type is specified',
      })
    }
    return intl.formatMessage(
      {
        id: 's2NQgbGD3T0CMM9FhGPf',
        defaultMessage: 'Share this {pageType}',
        description: 'Title for share modal, indicating the type of page being shared (e.g. offer, deal)',
      },
      { pageType: resolvedShareConfig.pageType },
    )
  }, [intl, resolvedShareConfig.pageType])

  const shareMessage = resolvedShareConfig.shareMessage || ''

  if (whiteLabelAppConfig.isApp) return ShareButton

  return (
    <>
      {ShareButton}
      <Modal
        title={modalTitle}
        isOpen={isOpen}
        onClose={closeShareModal}
      >
        <>
          {!hideEmail && mode === EMAIL_MODE && (<ShareByEmailForm
            onShare={onShare}
            closeShareModal={closeShareModal}
            shareMessage={shareMessage}
            shareConfig={resolvedShareConfig}
          />)}
          {mode !== EMAIL_MODE && (
            <Group direction="vertical" gap={16}>
              <div>
                <ShareThis onShare={onShare} shareConfig={resolvedShareConfig} />
                {!hideEmail && <ShareOption
                  kind="tertiary"
                  href={mailTo}
                  onClick={() => {
                    loggedIn ? setMode?.(EMAIL_MODE) : onShare('email')
                  }}
                  startIcon={<LineEnvelopeIcon />}
                >
                  {intl.formatMessage({
                    id: 'EcJlURpblUXvuAA-C7zt',
                    defaultMessage: 'Email',
                    description: 'Label for email share option in share modal for sharing content',
                  })}
                </ShareOption>}
              </div>
              {copyAvailable() && (<CopyLinkOuterContainer>
                <Group direction="horizontal" gap={0} verticalAlign="center">
                  <ShareLinkTextContainer onClick={copyToClipboard}>
                    <BodyText variant="small" wrap="truncate" colour="neutral-three">
                      {(shareUrl ?? resolvedShareConfig.url)?.replace('https://', '')}
                    </BodyText>
                  </ShareLinkTextContainer>
                  <TextButton kind="tertiary" onClick={copyToClipboard} startIcon={<LineLinkIcon />}>
                    {intl.formatMessage({
                      id: 'yOu4jfnY5WFNnDWg5F-s',
                      defaultMessage: 'Copy link',
                      description: 'Action button label to copy the share link to clipboard',
                    })}
                  </TextButton>
                </Group>
              </CopyLinkOuterContainer>
              )}
              {!!sharePoints && <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
                startIcon={<StackedLoyaltyTierIcon />}
              >
                {intl.formatMessage({
                  id: 'IIB9PrnT_7cB6GubpJin',
                  defaultMessage: 'Earn {points} points for sharing this offer',
                  description: 'Loyalty pill text showing points earned for sharing an offer with friends',
                }, { points: sharePoints })}
              </CaptionTextBlock>}
            </Group>
          )}
        </>
      </Modal>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    loggedIn: selectLoggedIn(state),
  }
}

const actions = {
  offerShareButtonClick,
  offerShareComplete,
}

export default connect(mapStateToProps, actions)(ShareModal)
